import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Skeleton, useToast } from '@bbpos/ui';
import { siteContentModerationService } from '@/services/site-content.service';
import { GLASS_PANEL, cn } from './tenant-ui';
import { SITE_PAGE_LABELS, type SitePageKey } from '@/types/site-content';

/**
 * The platform's only lever against a tenant publishing spam.
 *
 * Every storefront is a subdomain of ONE registrable domain, so thin or abusive
 * content taxes the whole zone's reputation — and `tenant_sites.allow_indexing`
 * is all-or-nothing for a whole site. This is the per-page version: a moderator
 * can hold `/about` out of search without unpublishing a shop's delivery
 * policy.
 *
 * A tenant cannot clear it. The API's tenant UPDATE never assigns the column,
 * so the hold survives a re-publish by construction rather than because a
 * service remembered to preserve it.
 *
 * It does NOT unpublish. Taking a shop's returns policy off their own website
 * is not ours to do; keeping it out of a search index is.
 */
export function TenantContentModerationPanel({
  tenantId,
  canModerate,
}: {
  tenantId: string;
  canModerate: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [noteFor, setNoteFor] = useState<SitePageKey | null>(null);
  const [note, setNote] = useState('');

  const { data: pages, isLoading, error } = useQuery({
    queryKey: ['tenant-site-content', tenantId],
    queryFn: () => siteContentModerationService.list(tenantId),
  });

  const setHold = useMutation({
    mutationFn: ({ key, hold, reason }: { key: SitePageKey; hold: boolean; reason?: string }) =>
      siteContentModerationService.setHold(tenantId, key, hold, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-site-content', tenantId] });
      setNoteFor(null);
      setNote('');
      toast({ title: 'Updated', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message || 'Could not update', variant: 'error' }),
  });

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (error) return <Alert variant="danger" description="Could not load this tenant’s pages." />;

  const list = pages ?? [];

  return (
    <div className={cn(GLASS_PANEL, 'p-6')}>
      <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
        Content pages
      </h3>
      <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
        What this shop has written. Holding a page keeps it out of search; it stays
        readable on their site, and they cannot clear the hold.
      </p>

      {list.length === 0 ? (
        <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
          This shop has not written any pages yet.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((page) => (
            <li
              key={page.page_key}
              className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-700"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-neutral-900 dark:text-white">
                  {SITE_PAGE_LABELS[page.page_key] ?? page.page_key}
                </span>
                <Badge variant={page.status === 'PUBLISHED' ? 'success' : 'neutral'}>
                  {page.status === 'PUBLISHED' ? 'Live' : 'Draft'}
                </Badge>
                {page.admin_noindex && <Badge variant="danger">Held</Badge>}
                {page.noindex && !page.admin_noindex && (
                  <Badge variant="warning">Owner chose noindex</Badge>
                )}
                <span className="ml-auto text-xs text-neutral-500 dark:text-neutral-400">
                  {page.title}
                </span>
              </div>

              {page.admin_note && (
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  Reason on file: {page.admin_note}
                </p>
              )}

              {canModerate && (
                <div className="mt-3">
                  {page.admin_noindex ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={setHold.isPending}
                      onClick={() =>
                        setHold.mutate({ key: page.page_key, hold: false, reason: undefined })
                      }
                    >
                      Release
                    </Button>
                  ) : noteFor === page.page_key ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Why (kept on file, not shown to the shop)"
                        maxLength={500}
                        className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-800"
                      />
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={setHold.isPending}
                        onClick={() =>
                          setHold.mutate({ key: page.page_key, hold: true, reason: note })
                        }
                      >
                        Hold
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setNoteFor(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setNoteFor(page.page_key);
                        setNote('');
                      }}
                    >
                      Hold out of search
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
